import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";

import { api } from "@/api/api";
import { useFeatureIsOn } from "@/app/featureFlags";
import type {
  ValidateFlightPromoCodeBodyInputDto,
  ValidateFlightPromoCodeOutputDto,
  ValidateHotelPromoCodeBodyInputDto,
  ValidateHotelPromoCodeOutputDto,
  ValidatePackagePromoCodeBodyInputDto,
  ValidatePackagePromoCodeOutputDto,
} from "@/types/codegen";
import { formatCurrency } from "@/utils/currency";

export type ValidationStatus = {
  type: "success" | "error" | "loading";
  message?: string;
};

type ParamsMap = {
  trips: ValidatePackagePromoCodeBodyInputDto;
  flights: ValidateFlightPromoCodeBodyInputDto;
  hotels: ValidateHotelPromoCodeBodyInputDto;
};

type OutputMap = {
  trips: ValidatePackagePromoCodeOutputDto;
  flights: ValidateFlightPromoCodeOutputDto;
  hotels: ValidateHotelPromoCodeOutputDto;
};

const serviceToUrlMap: Record<keyof ParamsMap, string> = {
  trips: "/packages/promo-codes/validate",
  flights: "/flights/promo-codes/validate",
  hotels: "/hotels/promo-codes/validate",
};

type Params<T extends keyof ParamsMap> = {
  service: T;
  validationRequestData: ParamsMap[T];
};

export function usePromoCodeValidation<T extends keyof ParamsMap>({
  service,
  validationRequestData,
}: Params<T>) {
  const intl = useIntl();

  const arePromoCodesEnabled = useFeatureIsOn("promo-codes");

  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: [
      service,
      "promo-code-validation",
      validationRequestData,
    ] as const,
    queryFn: ({ signal, queryKey: [, , payload] }) => {
      return api.post<OutputMap[T]>(serviceToUrlMap[service], payload, {
        signal,
      });
    },
    select: (response) => response.data.data,
    enabled: arePromoCodesEnabled && Boolean(validationRequestData.promoCode),
    refetchOnMount: true,
    staleTime: 0,
  });

  const validationStatus: ValidationStatus | undefined = isFetching
    ? { type: "loading" }
    : isError
      ? {
          type: "error",
          message: intl.formatMessage({ id: "promoCode.failedToApply" }),
        }
      : data?.result === false
        ? {
            type: "error",
            message:
              data.message || intl.formatMessage({ id: "promoCode.invalid" }),
          }
        : data?.result === true
          ? {
              type: "success",
              message: intl.formatMessage(
                { id: "promoCode.appliedSuccessfully" },
                { amount: formatCurrency(data.discountAmount) },
              ),
            }
          : undefined;

  return {
    validationStatus,
    data,
    revalidate: refetch,
  };
}
